import { NameOfVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode, getRangeForAllRecordsWithRecordingUuidAndRowUuid, type DbVideoFragment, type UUID } from "./DatabaseTypes";
import { requireEvidenceDatabase } from "./getDatabase";



export class DbVideoFragmentTableReader {
	get db() { return requireEvidenceDatabase(); };

	videoFragments = async () => await (await this.db).getAllFromIndex("videoFragments", NameOfVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode, getRangeForAllRecordsWithRecordingUuidAndRowUuid(this.recordingUuid));

	/**
	 * Iterate over all the fragments of a recording.
	 *
	 * We don't want to load them all into memory because they might be large.
	 *
	 * Databases support cursors for iterative operations, but we can't use IndexedDb's cursors.
	 * Were we to do so, the transaction would close while we waited the callback (e.g., awaiting
	 * the file system to write a blob to disk.)
	 *
	 * Instead, we will first grab all of the keys in one transaction (which should be fairly small).
	 * Then, we will iterate over each key, reading each (potentially large) blog into memory,
	 * executing the callback, and as we iterate to the next the current blob can be freed from memory.
	 *
	 * @param callback
	 */
	forRecordingFragmentsWithAsyncCallback = async (callback: (fragment: DbVideoFragment) => Promise<void>) => {
		const db = await this.db;
		const videoFragmentKeys = await db.getAllKeysFromIndex("videoFragments", NameOfVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode, getRangeForAllRecordsWithRecordingUuidAndRowUuid(this.recordingUuid));
		for (const key of videoFragmentKeys) {
			const fragment = await db.get("videoFragments", key);
			if (fragment != null) {
				await callback(fragment);
			}
		}
	};


	protected constructor(public readonly recordingUuid: UUID) { }

	static #forRecordingUuid = new Map<UUID, DbVideoFragmentTableReader>();
	static forRecordingUuid = (recordingUuid: UUID) => DbVideoFragmentTableReader.#forRecordingUuid.get(recordingUuid) ?? (() => {
		const evidenceDbForRecording = new DbVideoFragmentTableReader(recordingUuid);
		DbVideoFragmentTableReader.#forRecordingUuid.set(recordingUuid, evidenceDbForRecording);
		return evidenceDbForRecording;
	})();

	static removeRecordingUuid = (recordingUuid: UUID) => DbVideoFragmentTableReader.#forRecordingUuid.delete(recordingUuid);
}
